import {orderScenarioFeedbackJson} from "@/helpers/feedbackJsonHelpers";
import {
  getCertificateFeedback,
  getProductFeedback,
  getScenarioFeedback,
  setFeedback,
  setFeedbackJson,
  setScheduleIntent,
} from "@/redux/slices/mlSlices/chatSlice";

import {useCallback, useEffect, useMemo, useState} from "react";

import {useScoreValue} from "@hooks/ScoreValue";
import logoVioletEn from "@images/general/ava_trainer_violet_logo_EN.png";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {LeaveFeedbackPopup} from "@popups/simulationPopups/LeaveFeedbackPopup";
import {RootButton} from "@ui/buttons/RootButton";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {Spinner} from "@ui/general/Spinner";
import {H3} from "@ui/titles/H3";
import markdownit from "markdown-it";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router-dom";
import {Scrollbar} from "react-typescript-scrollbar";

import styles from "./styles.module.scss";

export const AiFeedbackPopup = (props) => {
  const {again, quit, withFeedback = true} = props;

  const [showFeedbackDetails, setShowFeedbackDetails] = useState(false);

  const dispatch = useDispatch();
  const {processFeedback, getScoreEnding} = useScoreValue();

  const level = useLocation()?.state?.level;
  const {openPopup} = usePopup();

  const {t} = useTranslation();

  const {
    successful,
    messages,
    feedback,
    feedback_score,
    feedback_json,
    max_feedback_score,
    certified,
    scenario_session_id,
    product_session_id,
    certificate_session_id,
    serverStatus,
    score,
    sessionState,
  } = useSelector((state) => state.ml.chat);
  const {demo, role} = useSelector((state) => state.common.candidate);
  const [currentFeedback, setCurrentFeedback] = useState("");
  const hasFeedbackJson =
    Array.isArray(feedback_json) && feedback_json.length > 0;
  const orderedFeedbackJson = useMemo(
    () => orderScenarioFeedbackJson(feedback_json),
    [feedback_json],
  );
  const hasFeedbackText = feedback !== "" && feedback !== "generated";
  const hasFeedbackData = hasFeedbackJson || hasFeedbackText;

  const handleClickLeaveFeedback = () => {
    openPopup(
      <LeaveFeedbackPopup
        handleTryAgain={again}
        handleQuit={quit}
      />,
      {
        closeButton: false,
      },
    );
  };

  const getFeedback = useCallback(async () => {
    dispatch(setScheduleIntent(null));
    dispatch(setFeedback(""));
    dispatch(setFeedbackJson(null));

    try {
      if (product_session_id) {
        await dispatch(getProductFeedback(product_session_id));
      } else if (scenario_session_id) {
        await dispatch(getScenarioFeedback(scenario_session_id));
      } else if (certificate_session_id) {
        await dispatch(getCertificateFeedback(certificate_session_id));
      }
    } catch (error) {
      console.log("Error receiving feedback: ", error);
    }
  }, [
    dispatch,
    product_session_id,
    scenario_session_id,
    certificate_session_id,
  ]);

  const calcScore = useCallback(() => {
    if (scenario_session_id) {
      return {
        title:
          level?.value === "training"
            ? t("popup.feedback.not_completed")
            : t("popup.feedback.unfortunately"),
        desc:
          level?.value === "training" ? null : t("popup.feedback.text_result"),
      };
    } else if (certificate_session_id) {
      return {
        title: certified
          ? t("popup.feedback.certified")
          : t("popup.feedback.unfortunately"),
        desc: null,
      };
    }
  }, [
    feedback,
    certified,
    level?.value,
    t,
    certificate_session_id,
    scenario_session_id,
  ]);

  useEffect(() => {
    if (messages.length !== 0 && withFeedback) {
      getFeedback();
    }
  }, [messages, withFeedback, getFeedback]);

  useEffect(() => {
    if (feedback && feedback !== "" && feedback !== "generated") {
      const md = markdownit({html: true});
      const html = md.render(feedback);

      setCurrentFeedback(html);
    } else {
      setCurrentFeedback("");
    }
  }, [feedback]);

  return (
    <div className={styles.feedbackPopup}>
      <div className={styles.feedbackLogo}>
        <img
          src={logoVioletEn}
          alt={"ava-logo"}
        />
      </div>

      <div className={styles.feedbackTitle}>
        {(certificate_session_id || scenario_session_id) &&
        withFeedback &&
        hasFeedbackData &&
        feedback_score !== null &&
        max_feedback_score !== null ? (
          <div className={styles.certificationTitle}>
            {certificate_session_id && (
              <H3>
                <span>{calcScore().title}</span>
              </H3>
            )}

            <div className={styles.certificationResult}>
              <RootDesc>
                <strong>{t("popup.feedback.your_result")}</strong>
              </RootDesc>

              <RootDesc>
                <span>
                  <b
                    style={{
                      color:
                        certified || successful ? "var(--green)" : "var(--red)",
                    }}
                  >
                    {feedback_score}
                  </b>{" "}
                  /{max_feedback_score}
                </span>
              </RootDesc>
            </div>
          </div>
        ) : (
          <H3>{t("popup.feedback.title")}</H3>
        )}

        {scenario_session_id &&
          sessionState === "ended" &&
          score > 0 &&
          withFeedback && (
            <RootDesc>
              <span
                dangerouslySetInnerHTML={{
                  __html: t("page.chat.schedule_bot_message"),
                }}
              />
            </RootDesc>
          )}

        {(serverStatus === "loading" || !hasFeedbackData) && withFeedback && (
          <RootDesc>
            <span
              style={{color: "var(--grey)"}}
              dangerouslySetInnerHTML={{
                __html: product_session_id
                  ? t("popup.feedback.product_loading_desc")
                  : t("popup.feedback.scenario_loading_desc"),
              }}
            />
          </RootDesc>
        )}

        {hasFeedbackData && product_session_id && withFeedback && (
          <div className={styles.feedbackGrade}>
            <RootDesc>
              <span>
                {`${score} ${
                  score === 1
                    ? t("popup.feedback.grade_1")
                    : t("popup.feedback.grade")
                } ${level?.question_count}`}
              </span>
            </RootDesc>
          </div>
        )}
      </div>

      <div
        className={styles.feedbackContent}
        style={{minHeight: withFeedback ? "300rem" : "auto"}}
      >
        {(serverStatus === "loading" || !hasFeedbackData) && withFeedback ? (
          <Spinner
            width={80}
            height={80}
          />
        ) : feedback === "generated" && !hasFeedbackJson && withFeedback ? (
          <div className={"mb-auto mt-auto"}>
            <RootDesc>
              <span
                style={{
                  textAlign: "center",
                  display: "inline-block",
                }}
                dangerouslySetInnerHTML={{
                  __html: t("popup.feedback.generated"),
                }}
              />
            </RootDesc>
          </div>
        ) : (
          <>
            <Scrollbar
              style={{width: "100%"}}
              units={"rem"}
              contentHeight={440}
              contentPadding={10}
              barColor={"var(--greyLight)"}
              barBorderColor={
                "linear-gradient(180deg, var(--border) 65%, transparent)"
              }
              barWidth={6}
              barRadius={8}
              thumbColor={
                "linear-gradient(0deg, rgb(209, 139, 203) 0%, rgb(209, 186, 222) 50%, rgb(209, 139, 203) 100%)"
              }
              thumbWidth={6}
              thumbRadius={8}
              mask
              maskSize={95}
            >
              {!hasFeedbackJson ? (
                <RootDesc>
                  <div
                    className={styles.feedbackMarkdown}
                    dangerouslySetInnerHTML={{
                      __html: withFeedback
                        ? processFeedback(currentFeedback || "")
                        : t("popup.feedback.without_feedback"),
                    }}
                  />
                </RootDesc>
              ) : (
                <RootDesc>
                  <div
                    className={`${styles.feedbackJson} ${product_session_id ? styles.productJson : ""}`}
                  >
                    <div>
                      <article>
                        <strong>{t("popup.feedback.summary_title")}</strong>
                      </article>

                      {orderedFeedbackJson[orderedFeedbackJson.length - 1]
                        .feedback !== "" && (
                        <span>
                          {
                            orderedFeedbackJson[orderedFeedbackJson.length - 1]
                              .feedback
                          }
                        </span>
                      )}
                    </div>

                    {showFeedbackDetails &&
                      orderedFeedbackJson.map((item, index, items) => {
                        const isLast = index === items.length - 1;

                        if (isLast) return null;

                        return (
                          <div key={item.header}>
                            <article>
                              {item.header !== "" && (
                                <strong>{item.header}</strong>
                              )}

                              <>
                                {item.score !== "" && !product_session_id ? (
                                  <b>
                                    {item.score || 0}
                                    {getScoreEnding(item.score || 0)}
                                  </b>
                                ) : (
                                  <b>{item.score || ""}</b>
                                )}
                              </>
                            </article>

                            {item.feedback !== "" && (
                              <span>{item.feedback}</span>
                            )}
                          </div>
                        );
                      })}

                    <button
                      style={{color: "var(--violet)"}}
                      onClick={() =>
                        setShowFeedbackDetails(!showFeedbackDetails)
                      }
                    >
                      <b>
                        {showFeedbackDetails
                          ? t("button.hide_feedback_details")
                          : t("button.show_feedback_details")}
                      </b>
                    </button>
                  </div>
                </RootDesc>
              )}
            </Scrollbar>

            {/* {certificate_session_id && calcScore().desc && (
              <div style={{marginTop: "auto"}}>
                <RootDesc>
                  <b style={{fontSize: "23rem"}}>{calcScore().desc}</b>
                </RootDesc>
              </div>
            )} */}
          </>
        )}
      </div>

      <div className={styles.feedbackBtns}>
        {(serverStatus === "loading" || !hasFeedbackData) && withFeedback ? (
          <RootButton
            type={"button"}
            onClick={quit}
            txt={
              demo && role === "Representative"
                ? t("button.close_chat")
                : t("button.exit_simulation")
            }
          />
        ) : (
          <>
            {!certificate_session_id && (
              <RootButton
                txt={t("button.again")}
                type={"button"}
                onClick={again}
              />
            )}

            <RootButton
              type={"button"}
              onClick={quit}
              txt={
                demo && role === "Representative"
                  ? t("button.close_chat")
                  : t("button.exit_simulation")
              }
            />

            {(window.location.host.includes("test") ||
              window.location.host.includes("localhost")) && (
              <RootButton
                type={"button"}
                onClick={handleClickLeaveFeedback}
                txt={t("button.leave_feedback_simulation")}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
